// Vertikale Tool-Leiste links. Klick setzt Tool, Tastenbindings sind in use-tool definiert.
// V3 UX-Pass: Tools in 4 Gruppen — Cursor / Visuals / Anforderungen (Flyout) / Struktur.
// Sticky-Button trägt Mini-Flyout für die 8 Farb-Presets; Anforderungen-Gruppen-Button
// öffnet ein Flyout mit Requirement/Pain/Dependency + Shortcut-Spalte (Miro-Style).
// Welle-1-Polish: Shape-Button + Icon-Button bekommen ebenfalls Flyouts (Pills bzw. IconPicker)
// damit der User VOR dem Insert eine Variante waehlen kann.
// Drop eines Elements resettet das Tool zurück auf "select" — siehe Canvas.tsx.

import type { ShapeKind, ShapeSemantics, StickyColor } from "@ms-whiteboard/shared";
import { type JSX, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconPicker } from "./IconPicker.js";
import { getIcon } from "./icon-cache.js";
import { BPMN_DEFAULT_KIND, BPMN_GLYPH, BPMN_SEMANTICS } from "./shape-renderer.js";
import type { Tool } from "./tool-state.js";

interface ToolRailProps {
  tool: Tool;
  setTool: (next: Tool) => void;
  stickyColor: StickyColor;
  setStickyColor: (next: StickyColor) => void;
  shapeKind: ShapeKind;
  setShapeKind: (next: ShapeKind) => void;
  shapeSemantics: ShapeSemantics | undefined;
  setShapeSemantics: (next: ShapeSemantics | undefined) => void;
  iconName: string;
  setIconName: (next: string) => void;
}

interface ToolDef {
  id: Tool;
  shortcut: string;
  icon: JSX.Element | string;
}

// Mauszeiger-Glyph fuer das Auswahl-Tool (lucide mouse-pointer, inline statt Lazy-Lucide,
// damit der 910-kB-Chunk nicht beim Board-Load gezogen wird).
const CURSOR_ICON: JSX.Element = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="m3 3 7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
  </svg>
);

// Mini-Mindmap (Wurzel links, zwei Kinder rechts) fuer das Mindmap-Tool.
const MINDMAP_ICON: JSX.Element = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="5" cy="12" r="2.2" />
    <circle cx="18" cy="6.5" r="2.2" />
    <circle cx="18" cy="17.5" r="2.2" />
    <path d="M7 11 14 7.5" />
    <path d="M7 13 14 16.5" />
  </svg>
);

const STICKY_COLORS: readonly StickyColor[] = [
  "yellow",
  "pink",
  "mint",
  "blue",
  "lavender",
  "peach",
  "gray",
  "white",
];

const SHAPE_KINDS: readonly ShapeKind[] = [
  "rounded-rect",
  "ellipse",
  "circle",
  "diamond",
  "hexagon",
  "parallelogram",
];

const SHAPE_KIND_GLYPH: Record<ShapeKind, string> = {
  "rounded-rect": "▢",
  ellipse: "◯",
  circle: "⬤",
  diamond: "◇",
  hexagon: "⬡",
  parallelogram: "▱",
};

const SELECT: ToolDef = { id: "select", shortcut: "V", icon: CURSOR_ICON };
const TEXT: ToolDef = { id: "text", shortcut: "T", icon: "T" };
const ARROW: ToolDef = { id: "arrow", shortcut: "A", icon: "↗" };
const FRAME: ToolDef = { id: "frame", shortcut: "F", icon: "▢" };
const MINDMAP: ToolDef = { id: "mindmap-node", shortcut: "M", icon: MINDMAP_ICON };

type SemanticToolId = "requirement" | "pain" | "dependency";

const SEMANTIC_TOOLS: readonly ToolDef[] = [
  { id: "requirement", shortcut: "Q", icon: "◧" },
  { id: "pain", shortcut: "P", icon: "✕" },
  { id: "dependency", shortcut: "D", icon: "⇄" },
];

const SEMANTIC_TOOL_BY_ID: Record<SemanticToolId, ToolDef> = {
  requirement: { id: "requirement", shortcut: "Q", icon: "◧" },
  pain: { id: "pain", shortcut: "P", icon: "✕" },
  dependency: { id: "dependency", shortcut: "D", icon: "⇄" },
};

function isSemantic(tool: Tool): tool is SemanticToolId {
  return tool === "requirement" || tool === "pain" || tool === "dependency";
}

type FlyoutKey = "sticky-colors" | "semantics" | "shape-kinds" | "icon-picker" | null;

export function ToolRail({
  tool,
  setTool,
  stickyColor,
  setStickyColor,
  shapeKind,
  setShapeKind,
  shapeSemantics,
  setShapeSemantics,
  iconName,
  setIconName,
}: ToolRailProps): JSX.Element {
  const { t } = useTranslation();
  const [openFlyout, setOpenFlyout] = useState<FlyoutKey>(null);
  const [lastSemantic, setLastSemantic] = useState<SemanticToolId>("requirement");
  const railRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isSemantic(tool)) setLastSemantic(tool);
  }, [tool]);

  useEffect(() => {
    if (!openFlyout) return;
    const onDown = (e: MouseEvent): void => {
      if (railRef.current && !railRef.current.contains(e.target as Node)) {
        setOpenFlyout(null);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpenFlyout(null);
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [openFlyout]);

  const pickTool = (next: Tool): void => {
    setTool(next);
    setOpenFlyout(null);
  };

  const pickStickyColor = (c: StickyColor): void => {
    setStickyColor(c);
    setTool("sticky");
    setOpenFlyout(null);
  };

  const pickShapeKind = (k: ShapeKind): void => {
    setShapeKind(k);
    setShapeSemantics(undefined);
    setTool("shape");
    setOpenFlyout(null);
  };

  const pickShapeSemantics = (sem: ShapeSemantics): void => {
    setShapeSemantics(sem);
    setShapeKind(BPMN_DEFAULT_KIND[sem]);
    setTool("shape");
    setOpenFlyout(null);
  };

  const pickIconName = (n: string): void => {
    setIconName(n);
    setTool("icon");
    setOpenFlyout(null);
  };

  const semanticActive = isSemantic(tool);
  const semanticDef = SEMANTIC_TOOL_BY_ID[lastSemantic];

  return (
    <nav className="canvas-tool-rail" aria-label="tools" ref={railRef}>
      <div className="tool-group">
        <PlainToolButton
          def={SELECT}
          active={tool === SELECT.id}
          onPick={() => setTool(SELECT.id)}
        />
      </div>

      <div className="tool-group tool-group-divider">
        <div className="tool-anchor">
          <button
            type="button"
            aria-pressed={tool === "sticky"}
            aria-haspopup="menu"
            aria-expanded={openFlyout === "sticky-colors"}
            className={`tool-button ${tool === "sticky" ? "is-active" : ""}`}
            onClick={() => {
              setTool("sticky");
              setOpenFlyout((p) => (p === "sticky-colors" ? null : "sticky-colors"));
            }}
            title={`${t("canvas.tool.sticky")} (S)`}
          >
            <span className={`tool-icon tool-icon-sticky is-${stickyColor}`} aria-hidden="true" />
            <span className="tool-shortcut" aria-hidden="true">
              S
            </span>
            <span className="tool-chevron" aria-hidden="true">
              ▸
            </span>
          </button>
          {openFlyout === "sticky-colors" && (
            <div className="tool-flyout tool-flyout-sticky" role="menu">
              <h5 className="tool-flyout-title">{t("inspector.section.color")}</h5>
              <div className="tool-flyout-color-grid">
                {STICKY_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    role="menuitem"
                    title={t(`inspector.color.${c}`)}
                    aria-label={t(`inspector.color.${c}`)}
                    aria-pressed={stickyColor === c}
                    className={`tool-color-swatch is-${c} ${stickyColor === c ? "is-active" : ""}`}
                    onClick={() => pickStickyColor(c)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        <PlainToolButton def={TEXT} active={tool === TEXT.id} onPick={() => pickTool(TEXT.id)} />
      </div>

      <div className="tool-group tool-group-divider">
        <PlainToolButton def={ARROW} active={tool === ARROW.id} onPick={() => pickTool(ARROW.id)} />
        <PlainToolButton def={FRAME} active={tool === FRAME.id} onPick={() => pickTool(FRAME.id)} />
        <PlainToolButton
          def={MINDMAP}
          active={tool === MINDMAP.id}
          onPick={() => pickTool(MINDMAP.id)}
        />
        <div className="tool-anchor">
          <button
            type="button"
            aria-pressed={tool === "shape"}
            aria-haspopup="menu"
            aria-expanded={openFlyout === "shape-kinds"}
            className={`tool-button ${tool === "shape" ? "is-active" : ""}`}
            onClick={() => {
              setTool("shape");
              setOpenFlyout((p) => (p === "shape-kinds" ? null : "shape-kinds"));
            }}
            title={`${t("canvas.tool.shape")} (H)`}
          >
            <span className="tool-icon" aria-hidden="true">
              {SHAPE_KIND_GLYPH[shapeKind]}
            </span>
            <span className="tool-shortcut" aria-hidden="true">
              H
            </span>
            <span className="tool-chevron" aria-hidden="true">
              ▸
            </span>
          </button>
          {openFlyout === "shape-kinds" && (
            <div className="tool-flyout tool-flyout-shape" role="menu">
              <h5 className="tool-flyout-title">{t("inspector.shape.kind")}</h5>
              <div className="tool-flyout-shape-grid">
                {SHAPE_KINDS.map((k) => (
                  <button
                    key={k}
                    type="button"
                    role="menuitem"
                    aria-pressed={shapeKind === k && !shapeSemantics}
                    className={`tool-shape-swatch ${shapeKind === k && !shapeSemantics ? "is-active" : ""}`}
                    onClick={() => pickShapeKind(k)}
                    title={t(`inspector.shape.kind.${k}`)}
                  >
                    <span className="tool-shape-swatch-icon">{SHAPE_KIND_GLYPH[k]}</span>
                  </button>
                ))}
              </div>
              <h5 className="tool-flyout-title">{t("canvas.tool.shape.bpmn")}</h5>
              <div className="tool-flyout-shape-grid">
                {BPMN_SEMANTICS.map((sem) => (
                  <button
                    key={sem}
                    type="button"
                    role="menuitem"
                    aria-pressed={shapeSemantics === sem}
                    className={`tool-shape-swatch ${shapeSemantics === sem ? "is-active" : ""}`}
                    onClick={() => pickShapeSemantics(sem)}
                    title={t(`shape.semantics.${sem.replace("bpmn:", "")}`)}
                  >
                    <span className="tool-shape-swatch-icon">{BPMN_GLYPH[sem]}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="tool-anchor">
          <button
            type="button"
            aria-pressed={tool === "icon"}
            aria-haspopup="menu"
            aria-expanded={openFlyout === "icon-picker"}
            className={`tool-button ${tool === "icon" ? "is-active" : ""}`}
            onClick={() => {
              setTool("icon");
              setOpenFlyout((p) => (p === "icon-picker" ? null : "icon-picker"));
            }}
            title={`${t("canvas.tool.icon")} (I)`}
          >
            <ToolIconPreview iconName={iconName} />
            <span className="tool-shortcut" aria-hidden="true">
              I
            </span>
            <span className="tool-chevron" aria-hidden="true">
              ▸
            </span>
          </button>
          {openFlyout === "icon-picker" && (
            <div className="tool-flyout tool-flyout-icon" role="menu">
              <h5 className="tool-flyout-title">{t("canvas.tool.icon")}</h5>
              <IconPicker value={iconName} onChange={pickIconName} />
            </div>
          )}
        </div>
      </div>

      {/* Anforderungs-Gruppe (Requirement/Pain/Dependency) ganz unten. */}
      <div className="tool-group tool-group-divider">
        <div className="tool-anchor">
          <button
            type="button"
            aria-pressed={semanticActive}
            aria-haspopup="menu"
            aria-expanded={openFlyout === "semantics"}
            className={`tool-button ${semanticActive ? "is-active" : ""}`}
            onClick={() => {
              setTool(lastSemantic);
              setOpenFlyout((p) => (p === "semantics" ? null : "semantics"));
            }}
            title={`${t("canvas.tool.group.requirements")} (${semanticDef.shortcut})`}
          >
            <span className="tool-icon" aria-hidden="true">
              {semanticDef.icon}
            </span>
            <span className="tool-shortcut" aria-hidden="true">
              {semanticDef.shortcut}
            </span>
            <span className="tool-chevron" aria-hidden="true">
              ▸
            </span>
          </button>
          {openFlyout === "semantics" && (
            <div className="tool-flyout tool-flyout-list" role="menu">
              <h5 className="tool-flyout-title">{t("canvas.tool.group.requirements")}</h5>
              <ul>
                {SEMANTIC_TOOLS.map((def) => (
                  <li key={def.id}>
                    <button
                      type="button"
                      role="menuitem"
                      className={`tool-flyout-item ${tool === def.id ? "is-active" : ""}`}
                      onClick={() => pickTool(def.id)}
                    >
                      <span className="tool-flyout-item-icon" aria-hidden="true">
                        {def.icon}
                      </span>
                      <span className="tool-flyout-item-label">{t(`canvas.tool.${def.id}`)}</span>
                      <span className="tool-flyout-item-shortcut" aria-hidden="true">
                        {def.shortcut}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}

interface PlainToolButtonProps {
  def: ToolDef;
  active: boolean;
  onPick: () => void;
}

function PlainToolButton({ def, active, onPick }: PlainToolButtonProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      aria-pressed={active}
      className={`tool-button ${active ? "is-active" : ""}`}
      onClick={onPick}
      title={`${t(`canvas.tool.${def.id}`)} (${def.shortcut})`}
    >
      <span className="tool-icon" aria-hidden="true">
        {def.icon}
      </span>
      <span className="tool-shortcut" aria-hidden="true">
        {def.shortcut}
      </span>
    </button>
  );
}

function ToolIconPreview({ iconName }: { iconName: string }): JSX.Element {
  const Cmp = getIcon(iconName);
  return Cmp ? (
    <span className="tool-icon">
      <Cmp size={18} />
    </span>
  ) : (
    <span className="tool-icon" aria-hidden="true">
      {iconName.charAt(0)}
    </span>
  );
}
